import { useId, useState } from 'react';
import { T, useGT } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import {
  splitColumnPath,
  type ColumnConfigEntry,
  type TaskColumnSource,
} from '@erp/shared-types';
import type { TaskTypeResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  SingleSelect,
  type SingleSelectOption,
} from 'src/components/common/SingleSelect';
import { useTaskAnchorFieldCatalog } from 'src/api/rpc/hooks/tasks/useTaskAnchorFieldCatalog';
import {
  buildPickedColumn,
  fieldOptionsForSource,
  humanizeColumnPath,
  type ColumnFormat,
  type TaskColumnFieldOption,
} from './taskColumnOptions';

type Gt = ReturnType<typeof useGT>;

function sourceOptions(
  gt: Gt,
  taskType: TaskTypeResponse | undefined,
): SingleSelectOption[] {
  const options: Array<SingleSelectOption & { id: TaskColumnSource }> = [
    {
      id: 'input',
      label: gt('Task input', { context: 'task column picker data source' }),
    },
    {
      id: 'correction',
      label: gt('Task correction', {
        context:
          'task column picker data source: the edited task input, falling back to the original input',
      }),
    },
    {
      id: 'outcomeData',
      label: gt('Outcome data', { context: 'task column picker data source' }),
    },
    {
      id: 'metadata',
      label: gt('Metadata', { context: 'task column picker data source' }),
    },
  ];
  if (taskType?.anchorEntityType) {
    options.push({
      id: 'anchor',
      label: gt('Anchor fields', {
        context:
          'task column picker data source: fields of the business record the task works on',
      }),
    });
  }
  return options;
}

function formatOptions(gt: Gt): SingleSelectOption[] {
  const formats: Array<{ id: ColumnFormat; label: string }> = [
    { id: 'string', label: gt('Text', { context: 'column value format' }) },
    { id: 'number', label: gt('Number', { context: 'column value format' }) },
    { id: 'integer', label: gt('Integer', { context: 'column value format' }) },
    {
      id: 'currency',
      label: gt('Currency', { context: 'column value format' }),
    },
    {
      id: 'date',
      label: gt('Date (10/16/2026)', {
        context: 'column value format, a short numeric date',
      }),
    },
    {
      id: 'date_medium',
      label: gt('Date (Oct 16, 2026)', {
        context: 'column value format, a date written out with the month name',
      }),
    },
  ];
  return formats;
}

function toColumnSource(id: string | undefined): TaskColumnSource {
  switch (id) {
    case 'correction':
    case 'outcomeData':
    case 'metadata':
    case 'anchor':
      return id;
    default:
      return 'input';
  }
}

function toColumnFormat(id: string | undefined): ColumnFormat | undefined {
  switch (id) {
    case 'string':
    case 'number':
    case 'integer':
    case 'currency':
    case 'date':
    case 'date_medium':
      return id;
    default:
      return undefined;
  }
}

/** Schema-backed field suggestions for the chosen source. */
function useFieldOptions(
  source: TaskColumnSource,
  taskType: TaskTypeResponse | undefined,
): TaskColumnFieldOption[] {
  const catalog = useTaskAnchorFieldCatalog(
    source === 'anchor' ? taskType?.anchorEntityType : null,
  );
  return fieldOptionsForSource(source, {
    taskType,
    anchorCatalog: catalog.data?.fields ?? [],
  });
}

/**
 * "Add column" form for task-page queues: pick a source (input, correction,
 * outcome data, metadata, anchor), then a schema field or a free-text dotted
 * path, an optional header, and a display format. Emits a non-sortable
 * `<source>.<path>` column entry.
 */
export function TaskColumnPicker({
  taskType,
  onAdd,
}: {
  taskType: TaskTypeResponse | undefined;
  onAdd: (column: Omit<ColumnConfigEntry, 'id'>) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const [source, setSource] = useState<TaskColumnSource>('input');
  const [path, setPath] = useState('');
  const [header, setHeader] = useState('');
  const [format, setFormat] = useState<ColumnFormat | undefined>(undefined);
  const fieldOptions = useFieldOptions(source, taskType);

  const sources = sourceOptions(gt, taskType);
  const formats = formatOptions(gt);
  const fieldSelectOptions = fieldOptions.map((option) => ({
    id: option.path,
    label: option.label,
  }));
  const normalizedPath = splitColumnPath(path).join('.');

  function pickField(option: SingleSelectOption | null) {
    const field = fieldOptions.find((f) => f.path === option?.id);
    setPath(field?.path ?? '');
    setHeader(field?.label ?? '');
    setFormat(field?.format);
  }

  function submit() {
    if (!normalizedPath) return;
    onAdd(buildPickedColumn({ source, path: normalizedPath, header, format }));
    setPath('');
    setHeader('');
    setFormat(undefined);
  }

  return (
    <div className="flex flex-col gap-2">
      <SingleSelect
        id={`${idPrefix}-source`}
        label={gt('Source', { context: 'task column picker field label' })}
        options={sources}
        value={sources.find((o) => o.id === source) ?? null}
        onChange={(o) => {
          setSource(toColumnSource(o?.id));
          setPath('');
          setHeader('');
          setFormat(undefined);
        }}
        size="sm"
        shouldAutoSort={false}
        fullWidth
      />
      {fieldSelectOptions.length > 0 && (
        <SingleSelect
          id={`${idPrefix}-field`}
          label={gt('Field', { context: 'task column picker field label' })}
          options={fieldSelectOptions}
          value={
            fieldSelectOptions.find((o) => o.id === normalizedPath) ?? null
          }
          onChange={pickField}
          placeholder={gt('Select a field', {
            context: 'task column picker field placeholder',
          })}
          searchPlaceholder={gt('Search fields…', {
            context: 'search box in the task column picker field list',
          })}
          size="sm"
          shouldAutoSort={false}
          fullWidth
        />
      )}
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${idPrefix}-path`} className="text-xs">
          <T context="task column picker field label for a dotted JSON path">
            Path
          </T>
        </Label>
        <Input
          id={`${idPrefix}-path`}
          value={path}
          onChange={(e) => setPath(e.target.value)}
          placeholder={gt('e.g. supplier.name', {
            context:
              'example dotted path placeholder in the task column picker',
          })}
          className="h-8 font-mono text-xs"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${idPrefix}-header`} className="text-xs">
          <T context="task column picker field label for the column title">
            Header
          </T>
        </Label>
        <Input
          id={`${idPrefix}-header`}
          value={header}
          onChange={(e) => setHeader(e.target.value)}
          placeholder={normalizedPath ? humanizeColumnPath(normalizedPath) : ''}
          className="h-8 text-xs"
        />
      </div>
      <SingleSelect
        id={`${idPrefix}-format`}
        label={gt('Format', { context: 'task column picker field label' })}
        options={formats}
        value={formats.find((o) => o.id === format) ?? null}
        onChange={(o) => setFormat(toColumnFormat(o?.id))}
        placeholder={gt('Automatic', {
          context: 'task column picker format placeholder, no explicit format',
        })}
        size="sm"
        shouldAutoSort={false}
        allowClear
        fullWidth
      />
      <Button size="sm" disabled={!normalizedPath} onClick={submit}>
        <PlusIcon className="h-4 w-4" />
        <T context="button, add a custom column to the task queue table">
          Add Column
        </T>
      </Button>
    </div>
  );
}
